import { buildUrl, type QueryParams } from '@bank/utils/buildUrl';
import { createStorage } from '@bank/utils/createStorage';
import { typedEntries } from '@bank/utils/typedEntries';

import { FetchError } from './fetchError';

type ResponseType = 'json' | 'text' | 'blob' | 'arrayBuffer' | 'void';

interface ResponseMap<TData> {
  json: TData;
  text: string;
  blob: Blob;
  arrayBuffer: ArrayBuffer;
  void: null;
}

type ResponseByType<
  TRespType extends ResponseType,
  TData,
> = ResponseMap<TData>[TRespType];

interface RequestParams<TRespType extends ResponseType = 'json'> {
  auth?: boolean;
  body?: unknown;
  headers?: HeadersInit;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: QueryParams;
  responseType?: TRespType;
  signal?: AbortSignal;
  url: string;
}

/** Successful response wrapper. */
export interface ApiResponse<TData> {
  data: TData;
  status: number;
  statusText: string;
}

type GetParams<TRespType extends ResponseType = 'json'> = Omit<
  RequestParams<TRespType>,
  'body' | 'method'
>;

type MutationParams<TRespType extends ResponseType = 'json'> = Omit<
  RequestParams<TRespType>,
  'method' | 'params'
>;

/** Options passed to {@link createFetchClient}. */
interface FetchClientOptions {
  /** Application client ID, sent as `ClientId` header and used in `x-correlation-id`. */
  clientId: string;
  /** Optional async function that returns a bearer token. When omitted, auth headers are never sent. */
  getToken?: () => Promise<string>;
  /**
   * Optional global error callback invoked whenever a {@link FetchError} is thrown.
   * May be async; the returned promise is not awaited and the error is still thrown.
   */
  onError?: (error: FetchError) => void | Promise<void>;
}

interface FetchClient {
  get: <TData = unknown, TRespType extends ResponseType = 'json'>(
    params: GetParams<TRespType>,
  ) => Promise<ApiResponse<ResponseByType<TRespType, TData>>>;
  post: <TData = unknown, TRespType extends ResponseType = 'json'>(
    params: MutationParams<TRespType>,
  ) => Promise<ApiResponse<ResponseByType<TRespType, TData>>>;
  put: <TData = unknown, TRespType extends ResponseType = 'json'>(
    params: MutationParams<TRespType>,
  ) => Promise<ApiResponse<ResponseByType<TRespType, TData>>>;
  patch: <TData = unknown, TRespType extends ResponseType = 'json'>(
    params: MutationParams<TRespType>,
  ) => Promise<ApiResponse<ResponseByType<TRespType, TData>>>;
  del: <TData = unknown, TRespType extends ResponseType = 'json'>(
    params: MutationParams<TRespType>,
  ) => Promise<ApiResponse<ResponseByType<TRespType, TData>>>;
}

const sessionIdStorage = createStorage({
  key: 'bank-foundation-fetch-session-id',
  area: 'session',
  raw: true,
});

/**
 * Returns a stable session ID for the current browser tab session.
 * Falls back to an in-memory timestamp when sessionStorage is unavailable.
 */
const getOrCreateSessionId = (): string => {
  const existingSessionId = sessionIdStorage.get();

  if (existingSessionId) {
    return existingSessionId;
  }

  const sessionId = new Date().toISOString();
  sessionIdStorage.set(sessionId);

  return sessionId;
};

/**
 * Extracts key-value pairs from any {@link HeadersInit} source.
 */
const headerEntries = (source: HeadersInit): [string, string][] => {
  if (source instanceof Headers) {
    return [...source.entries()];
  }
  if (Array.isArray(source)) {
    return source;
  }
  return typedEntries(source);
};

/**
 * Merges multiple {@link HeadersInit} sources into a single {@link Headers}
 * instance. Later sources take precedence over earlier ones.
 * Handles plain objects, `Headers` instances, and `[key, value][]` tuples.
 */
const mergeHeaders = (...sources: (HeadersInit | undefined)[]): Headers => {
  const merged = new Headers();
  sources.forEach((source) => {
    if (source) {
      headerEntries(source).forEach(([key, value]) => {
        merged.set(key, value);
      });
    }
  });
  return merged;
};

/**
 * Returns `true` if the value is a native {@link BodyInit} type that
 * `fetch()` can consume directly without serialisation.
 */
const isBodyInit = (value: unknown): value is BodyInit =>
  typeof value === 'string' ||
  value instanceof Blob ||
  value instanceof ArrayBuffer ||
  value instanceof FormData ||
  value instanceof URLSearchParams ||
  (typeof ReadableStream !== 'undefined' && value instanceof ReadableStream) ||
  ArrayBuffer.isView(value);

/**
 * Serialises a request body and returns the appropriate content-type header.
 * Native {@link BodyInit} values are passed through unchanged.
 * Plain objects/arrays are JSON-stringified with `application/json`.
 */
const serializeBody = (
  body: unknown,
): { body: BodyInit | null; contentType?: string } => {
  if (body === undefined || body === null) {
    return { body: null };
  }
  if (isBodyInit(body)) {
    return { body };
  }
  return { body: JSON.stringify(body), contentType: 'application/json' };
};

/**
 * Attempts to read and parse the error response body.
 * Returns parsed JSON if possible, plain text otherwise, or undefined on failure.
 */
const parseErrorBody = async (res: Response): Promise<unknown> => {
  try {
    const text = await res.text();
    if (!text) {
      return undefined;
    }
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return undefined;
  }
};

/**
 * Reads the response body according to the requested type.
 */
const parseResponseBody = async <TData, TRespType extends ResponseType>(
  res: Response,
  responseType: TRespType,
  url: string,
  correlationId: string,
): Promise<ResponseByType<TRespType, TData>> => {
  switch (responseType) {
    case 'text':
      return (await res.text()) as ResponseByType<TRespType, TData>;
    case 'blob':
      return (await res.blob()) as ResponseByType<TRespType, TData>;
    case 'arrayBuffer':
      return (await res.arrayBuffer()) as ResponseByType<TRespType, TData>;
    case 'void':
      return null as ResponseByType<TRespType, TData>;
    default: {
      const text = await res.text();
      try {
        return (text ? JSON.parse(text) : null) as ResponseByType<
          TRespType,
          TData
        >;
      } catch {
        throw new FetchError(`Failed to parse JSON response from ${url}`, {
          correlationId,
          data: text,
          headers: res.headers,
          requestUrl: url,
          status: res.status,
        });
      }
    }
  }
};

/**
 * Creates a configured fetch client.
 *
 * @example
 * ```ts
 * const fetchClient = createFetchClient({
 *   clientId: 'my-app',
 *   getToken: () => auth.getToken(),
 * });
 *
 * const { data } = await fetchClient.get<User[]>({ url: '/api/users' });
 * ```
 */
export const createFetchClient = (options: FetchClientOptions): FetchClient => {
  const { clientId, getToken, onError } = options;
  const sessionId = getOrCreateSessionId();

  const getXCorrelationId = () =>
    `${clientId}-${sessionId}-${crypto.randomUUID()}`;

  async function request<
    TData = unknown,
    TRespType extends ResponseType = 'json',
  >(
    params: RequestParams<TRespType>,
  ): Promise<ApiResponse<ResponseByType<TRespType, TData>>> {
    const xCorrelationId = getXCorrelationId();
    const {
      url,
      method,
      body,
      headers,
      signal,
      params: queryParams,
      auth: requiresAuth = true,
    } = params;

    // `json` is the documented default for `TRespType`, so the cast is safe.
    const responseType = params.responseType ?? ('json' as TRespType);

    const requestUrl = buildUrl(url, queryParams);

    const { body: serializedBody, contentType } = serializeBody(body);

    const authHeaders: HeadersInit =
      requiresAuth && getToken
        ? { Authorization: `Bearer ${await getToken()}` }
        : {};

    // Precedence (last wins): defaults → auth → body content-type → caller
    const mergedHeaders = mergeHeaders(
      { ClientId: clientId, 'x-correlation-id': xCorrelationId },
      authHeaders,
      contentType ? { 'Content-Type': contentType } : undefined,
      headers,
    );

    const res = await globalThis.fetch(requestUrl, {
      body: serializedBody,
      headers: mergedHeaders,
      method,
      signal,
    });

    const correlationId = res.headers.get('x-correlation-id') ?? xCorrelationId;

    if (!res.ok) {
      const error = new FetchError(`HTTP error ${res.status}`, {
        correlationId,
        data: await parseErrorBody(res),
        headers: res.headers,
        requestUrl,
        status: res.status,
      });
      // Fire-and-forget: a promise-returning handler is intentionally not awaited.
      void onError?.(error);
      throw error;
    }

    const data = await parseResponseBody<TData, TRespType>(
      res,
      responseType,
      requestUrl,
      correlationId,
    ).catch((error: unknown) => {
      if (error instanceof FetchError) {
        // Fire-and-forget: a promise-returning handler is intentionally not awaited.
        void onError?.(error);
      }
      throw error;
    });

    return { data, status: res.status, statusText: res.statusText };
  }

  return {
    get: <TData = unknown, TRespType extends ResponseType = 'json'>(
      params: GetParams<TRespType>,
    ) => request<TData, TRespType>({ ...params, method: 'GET' }),

    post: <TData = unknown, TRespType extends ResponseType = 'json'>(
      params: MutationParams<TRespType>,
    ) => request<TData, TRespType>({ ...params, method: 'POST' }),

    put: <TData = unknown, TRespType extends ResponseType = 'json'>(
      params: MutationParams<TRespType>,
    ) => request<TData, TRespType>({ ...params, method: 'PUT' }),

    patch: <TData = unknown, TRespType extends ResponseType = 'json'>(
      params: MutationParams<TRespType>,
    ) => request<TData, TRespType>({ ...params, method: 'PATCH' }),

    del: <TData = unknown, TRespType extends ResponseType = 'json'>(
      params: MutationParams<TRespType>,
    ) => request<TData, TRespType>({ ...params, method: 'DELETE' }),
  };
};
